import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Check, Mic, Video, Image, FileText, Clock } from 'lucide-react';
import { useOptionalAuth } from '@/hooks/useOptionalAuth';
import { getMilestone, type MilestoneCriterion } from '@/lib/points';
import MilestoneEmblem from '@/components/MilestoneEmblem';

const ICON: Record<string, React.ReactNode> = {
  audio_hours: <Clock className="w-4 h-4 text-emerald-600" />,
  audio_count: <Mic className="w-4 h-4 text-emerald-600" />,
  video_count: <Video className="w-4 h-4 text-emerald-600" />,
  image_count: <Image className="w-4 h-4 text-emerald-600" />,
  text_count: <FileText className="w-4 h-4 text-emerald-600" />,
};

const ROUTE: Record<string, string> = {
  audio_hours: '/upload/audio',
  audio_count: '/upload/audio',
  video_count: '/upload/video',
  image_count: '/upload/image',
  text_count: '/upload/text',
};

const pct = (c: MilestoneCriterion) =>
  c.target <= 0 ? 100 : Math.min(100, Math.round((c.current / c.target) * 100));

const MilestoneProgress: React.FC<{ compact?: boolean }> = () => {
  const { t } = useTranslation();
  const auth = useOptionalAuth();
  const token = auth?.token;
  const user = auth?.user;
  const ident = user?.id ?? user?.username;

  const { data, isLoading } = useQuery({
    queryKey: ['points', 'milestone', ident],
    queryFn: () => getMilestone(token as string, String(ident)),
    enabled: !!token && !!ident,
  });

  // Hide the whole bar when there is no next milestone.
  if (
    isLoading ||
    !data ||
    data.current_level === undefined ||
    data.next_level === null
  )
    return null;

  const remaining = data.criteria.filter((c) => !c.met);
  const met = data.criteria.filter((c) => c.met);
  const total = data.criteria.length;

  return (
    <div className="max-w-xl mx-auto w-full bg-white/85 backdrop-blur-sm border border-slate-200 rounded-2xl px-3.5 py-2.5 sm:px-4 sm:py-3.5 shadow-sm">
      <div className="flex items-center justify-between gap-3 mb-2 sm:mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <MilestoneEmblem
            level={data.current_level}
            size={38}
            state="current"
          />
          <div className="leading-tight min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 leading-none mb-1">
              {t('common.current.milestone')}
            </p>
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 truncate">
              {data.current_title}
              <span className="flex-shrink-0 text-[10px] font-semibold text-blue-700 bg-blue-50 rounded-full px-1.5 py-0.5">
                L{data.current_level}
              </span>
            </p>
          </div>
        </div>
        <p className="text-[11px] leading-snug text-slate-500 text-right flex-shrink-0 max-w-[45%]">
          {met.length} of {total} {t('common.requirements.met.for')}{' '}
          <span className="font-semibold text-slate-700">
            L{data.next_level} · {data.next_name}
          </span>
        </p>
      </div>

      {remaining.length > 0 && (
        <div className="grid gap-1.5 sm:gap-2">
          {remaining.map((c) => (
            <a
              key={c.key}
              href={ROUTE[c.key]}
              className="flex items-center gap-2.5 border border-slate-200 rounded-xl px-3 py-1.5 sm:py-2 hover:bg-slate-50 hover:border-emerald-300 transition-colors"
            >
              <span className="flex-shrink-0">{ICON[c.key]}</span>
              <span className="w-16 sm:w-20 text-xs font-medium text-slate-700 truncate">
                {c.label}
              </span>
              <span className="flex-1 h-2 rounded-full bg-emerald-100 overflow-hidden">
                <span
                  className="block h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-[width] duration-500 ease-out"
                  style={{ width: `${pct(c)}%` }}
                />
              </span>
              <span className="text-[11px] font-medium tabular-nums text-slate-500 min-w-[52px] text-right">
                {Math.floor(c.current)} / {Math.round(c.target)}
              </span>
            </a>
          ))}
        </div>
      )}

      {met.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {met.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-800 bg-emerald-50 px-2 py-1 rounded-full"
            >
              <Check className="w-3 h-3" />
              {c.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default MilestoneProgress;
